import React, { useEffect, useRef, useState } from "react";
import { Lock, ArrowRight } from "lucide-react";

const PIN = "7427";
const STORAGE_KEY = "dw_site_unlocked";
const PIN_LENGTH = 4;

export function useSiteLock() {
  const [unlocked, setUnlocked] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(STORAGE_KEY) === "1";
  });
  function unlock() {
    localStorage.setItem(STORAGE_KEY, "1");
    setUnlocked(true);
  }
  return { unlocked, unlock };
}

export default function SiteLock({ onUnlock }) {
  const [digits, setDigits] = useState(Array(PIN_LENGTH).fill(""));
  const [error, setError] = useState(false);
  const inputs = useRef([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  function setDigit(i, val) {
    const clean = val.replace(/\D/g, "").slice(0, 1);
    const next = [...digits];
    next[i] = clean;
    setDigits(next);
    setError(false);
    if (clean && i < PIN_LENGTH - 1) inputs.current[i + 1]?.focus();
    if (next.join("").length === PIN_LENGTH) submit(next.join(""));
  }

  function handleKey(i, e) {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) inputs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < PIN_LENGTH - 1) inputs.current[i + 1]?.focus();
  }

  function handlePaste(e) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, PIN_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = pasted.split("").concat(Array(PIN_LENGTH).fill("")).slice(0, PIN_LENGTH);
    setDigits(next);
    if (next.join("").length === PIN_LENGTH) submit(next.join(""));
  }

  function submit(value) {
    if (value === PIN) {
      onUnlock();
    } else {
      setError(true);
      setTimeout(() => {
        setDigits(Array(PIN_LENGTH).fill(""));
        inputs.current[0]?.focus();
      }, 500);
    }
  }

  return (
    <div className="min-h-screen dw-navy-bg flex items-center justify-center px-6 relative overflow-hidden" data-testid="site-lock">
      <div className="dw-grid-lines absolute inset-0 opacity-10" />
      {/* Faint gold aura */}
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[var(--dw-gold)]/8 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[var(--dw-gold)]/5 blur-3xl" />

      <div className="relative w-full max-w-md text-center dw-fade-up">
        <div className="inline-flex items-center justify-center w-14 h-14 border border-[var(--dw-gold)]/40 rounded-sm mb-8 bg-[var(--dw-gold)]/8">
          <Lock className="w-6 h-6 text-[var(--dw-gold)]" strokeWidth={1.5} />
        </div>

        <div className="overline text-[var(--dw-gold)] mb-3">Private preview</div>
        <h1 className="font-serif text-4xl md:text-5xl text-[var(--dw-parchment)] mb-3 tracking-tight leading-tight">
          DW Training Academy
        </h1>
        <p className="text-white/60 mb-10 text-sm">
          Enter the 4-digit access code to continue.
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); submit(digits.join("")); }}
          className={`flex justify-center gap-3 mb-6 ${error ? "animate-shake" : ""}`}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => handleKey(i, e)}
              onPaste={handlePaste}
              data-testid={`pin-input-${i}`}
              className={`w-16 h-20 text-center text-3xl font-serif rounded-sm bg-white/5 text-[var(--dw-parchment)] border ${
                error
                  ? "border-red-400/70 bg-red-500/10"
                  : d
                  ? "border-[var(--dw-gold)]/70 bg-[var(--dw-gold)]/8"
                  : "border-white/15"
              } focus:outline-none focus:border-[var(--dw-gold)] transition-colors`}
            />
          ))}
        </form>

        {error && (
          <div className="text-sm text-red-300 mb-4" data-testid="pin-error">
            Incorrect code — try again.
          </div>
        )}

        <div className="text-xs text-white/40 mt-8">
          This preview is invitation-only. Please contact the program director for access.
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-8px); }
          40%, 80% { transform: translateX(8px); }
        }
        .animate-shake { animation: shake 400ms ease-in-out; }
      `}</style>
    </div>
  );
}
